import { useMutation, useQueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { ONE_HOUR_IN_MS } from '@/src/general/constants/time'
import { storage, STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'

import { getMeQueryOptions } from '../../auth/services/useMeQuery'
import type { INTENTION_TYPE } from '../../intention/models/Intention.enums'
import { getNotificationSettingsQueryOptions } from '../../notification/services/useNotificationSettingsQuery'
import type { TBecomeWatchmanPayload } from '../api/types/becomeWatchman.types'
import { watchmanApi } from '../api/watchmanApi'

type TWatchmanInitialIntentionStore = {
  intentionId?: number
  intentionType?: INTENTION_TYPE
  timestamp?: number
  saveIntention: (intentionId: number, intentionType: INTENTION_TYPE) => void
}

export const useWatchmanInitialIntentionStore = create(
  persist<TWatchmanInitialIntentionStore>(
    (set) => ({
      saveIntention: (intentionId: number, intentionType: INTENTION_TYPE) =>
        set({ intentionId, intentionType, timestamp: Date.now() }),
    }),
    { name: STORAGE_KEYS.WATCHMAN_INITIAL_INTENTION, storage: createJSONStorage(() => storage) },
  ),
)

export const useBecomeWatchman = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      const { intentionId, intentionType, timestamp } = useWatchmanInitialIntentionStore.getState()
      const payload: TBecomeWatchmanPayload =
        intentionId && timestamp && Date.now() - timestamp < ONE_HOUR_IN_MS ? { intentionId, intentionType } : {}

      return watchmanApi.becomeWatchman(payload)
    },
    onSuccess: () => {
      queryClient.setQueryData(
        getMeQueryOptions().queryKey,
        (old) =>
          old && {
            ...old,
            notifyWatchman: 'email' as const,
          },
      )
      queryClient.invalidateQueries(getMeQueryOptions())
      queryClient.invalidateQueries(getNotificationSettingsQueryOptions())
    },
  })
}
